/**
 * Mailtext, wie der Betreiber ihn lesen soll — statt wie der Absender-Client ihn technisch codiert.
 *
 * ── Warum es diese Datei gibt (Antoine, Posteingang-Verständlichkeit, 30.08.2026) ──────────────
 * Zwei konkrete, im Screenshot belegte Stolpersteine:
 *
 * 1. **RFC-2047-Betreffe.** Manche eingehenden Mails zeigten den Betreff so, wie er auf der Leitung
 *    steht: `=?utf-8?B?QW5mcmFnZSBmw7xyIEhvY2h6ZWl0?=` statt "Anfrage für Hochzeit". Der native
 *    Ingest-Pfad (Cloudflare Email Worker, `lib/inbound/mime-parser.ts` → `postal-mime`) dekodiert
 *    Betreffe bereits korrekt (`decodeWords`, RFC-2047-konform) — GEPRÜFT, kein Bug in der Nutzung.
 *    Der einfache JSON-Webhook (`routes/inbound-mail.ts`, für Provider ohne volles MIME-Parsing)
 *    und der künftige Postfach-Abholung-Pfad (`routes/mailbox-ingest.ts`, Runner kommt mit P2)
 *    nehmen `subject` dagegen als rohen String entgegen — OHNE Dekodierung. Bereits gespeicherte
 *    Zeilen aus diesen Pfaden bleiben also roh in der DB (kein Backfill, kein Datenverlust-Risiko).
 *    Diese Funktion ist deshalb eine reine ANZEIGE-Dekodierung: rein additiv, idempotent (ein
 *    bereits dekodierter Text ohne "=?...?=" kommt unverändert zurück), NIE ein DB-Schreibpfad.
 * 2. **Mail-Client-Artefakte.** Outlooks automatische Bildbeschreibung für eingebettete Bilder
 *    ("Barrierefreiheit"-Funktion) landet als Klammertext in der Text-Fassung, z.B.
 *    "[Ein Bild, das Text, Schrift, weiß enthält. KI-generierte Inhalte können fehlerhaft sein.]" —
 *    für den Betreiber nur Rauschen, das den eigentlichen Nachrichtentext verdeckt.
 *
 * Beide Funktionen sind rein (kein DOM/Netz), ändern NIE die gespeicherten Rohdaten — sie laufen
 * ausschließlich beim RENDERN in posteingang.tsx (Listentitel, Detail-Betreff, Nachrichtentext).
 */

/** Erkennt ueberhaupt ein RFC-2047-"encoded word" — Schnellpfad, damit der haeufige (bereits
 *  klartextige) Fall keine Regex-Ersetzung durchlaeuft. */
const ENTHAELT_ENCODED_WORD = /=\?[^?\s]+\?[BbQq]\?[^?]*\?=/;

/** Ein einzelnes "encoded word": `=?charset?B|Q?text?=` (RFC 2047 §2). */
const ENCODED_WORD = /=\?([^?\s]+)\?([BbQq])\?([^?]*)\?=/g;

/** RFC 2047 §2: Whitespace, das AUSSCHLIESSLICH zwei benachbarte encoded words trennt, gilt beim
 *  Anzeigen als nicht vorhanden (sonst reisst ein einzelnes Wort, das der Absender-Client auf
 *  mehrere encoded words aufgeteilt hat, wieder auseinander). */
function benachbarteWoerterZusammenziehen(text: string): string {
  return text.replace(/(\?=)[ \t]+(?==\?)/g, "$1");
}

function base64ZuBytes(base64: string): Uint8Array {
  const bereinigt = base64.replace(/[^A-Za-z0-9+/=]/g, "");
  const binaer = atob(bereinigt);
  const bytes = new Uint8Array(binaer.length);
  for (let i = 0; i < binaer.length; i++) bytes[i] = binaer.charCodeAt(i);
  return bytes;
}

/** RFC-2047-Q-Encoding: wie Quoted-Printable, aber "_" statt "=20" fuer ein Leerzeichen. */
function quotedPrintableZuBytes(text: string): Uint8Array {
  const mitLeerzeichen = text.replace(/_/g, " ");
  const bytes: number[] = [];
  for (let i = 0; i < mitLeerzeichen.length; i++) {
    const zeichen = mitLeerzeichen[i]!;
    if (zeichen === "=" && /^[0-9A-Fa-f]{2}$/.test(mitLeerzeichen.slice(i + 1, i + 3))) {
      bytes.push(parseInt(mitLeerzeichen.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      bytes.push(zeichen.charCodeAt(0));
    }
  }
  return new Uint8Array(bytes);
}

function bytesZuText(bytes: Uint8Array, charset: string): string {
  try {
    return new TextDecoder(charset.toLowerCase()).decode(bytes);
  } catch {
    // Unbekannter/nicht unterstuetzter Zeichensatz (z.B. eine seltene Provider-Eigenheit) -
    // windows-1252 deckt den weit ueberwiegenden Teil lateinischer Mails ab und crasht nie.
    try {
      return new TextDecoder("windows-1252").decode(bytes);
    } catch {
      return new TextDecoder().decode(bytes);
    }
  }
}

/**
 * Dekodiert RFC-2047-"encoded words" in einem Mail-Header-Wert (Betreff, Absendername) fuer die
 * ANZEIGE. `null`/`undefined` bleibt `null`; ein Text ohne encoded words kommt unveraendert
 * zurueck (Schnellpfad UND Idempotenz - zweifaches Dekodieren aendert nichts mehr).
 *
 * Schlaegt das Dekodieren EINES Worts fehl (z.B. abgeschnittenes Base64), bleibt genau dieses
 * Wort roh stehen statt die ganze Funktion zu werfen oder den Text leerzuraeumen - lieber einmal
 * haesslich als einmal falsch/verschluckt.
 */
export function dekodiereMailBetreff(text: string | null | undefined): string | null {
  if (text == null) return null;
  if (!ENTHAELT_ENCODED_WORD.test(text)) return text;
  const zusammengezogen = benachbarteWoerterZusammenziehen(text);
  return zusammengezogen.replace(ENCODED_WORD, (treffer, charset: string, encoding: string, payload: string) => {
    try {
      const bytes = encoding.toUpperCase() === "B" ? base64ZuBytes(payload) : quotedPrintableZuBytes(payload);
      return bytesZuText(bytes, charset);
    } catch {
      return treffer;
    }
  });
}

/**
 * Bekannte Muster automatisch generierter Bildbeschreibungen, wie sie Outlook/Word (Windows'
 * "Automatische Beschreibungen") in die TEXT-Fassung einer Mail einbettet. Bewusst NICHT "jeder
 * Klammertext" - das wuerde legitime, vom Menschen geschriebene Anmerkungen in eckigen Klammern
 * mitloeschen. Jedes Muster ist an einen konkreten, wiederkehrenden Wortlaut gebunden:
 *
 * 1. Deutsch, mit KI-Disclaimer (das Screenshot-Beispiel):
 *    "[Ein Bild, das Text, Schrift, weiß enthält. KI-generierte Inhalte können fehlerhaft sein.]"
 * 2. Deutsch, aeltere Variante ohne Disclaimer:
 *    "[Ein Bild, das ... enthält. Automatisch generierte Beschreibung]"
 * 3. Englisch:
 *    "[A picture containing ... Description automatically generated ...]"
 */
const OUTLOOK_ALT_TEXT_MUSTER: RegExp[] = [
  /\[\s*Ein Bild,[^[\]]*?enthält\.[^[\]]*?\]/gi,
  /\[[^[\]]*?Automatisch generierte Beschreibung[^[\]]*?\]/gi,
  /\[[^[\]]*?Description automatically generated[^[\]]*?\]/gi,
];

/**
 * Entfernt bekannte Mail-Client-Artefakte aus einem Nachrichtentext fuer die ANZEIGE.
 * `null`/`undefined` bleibt `null`. Raeumt anschliessend nur die Leerzeilen/Leerzeichen auf, die
 * das Entfernen selbst hinterlassen kann - der Rest der Formatierung bleibt unangetastet.
 */
export function entferneMailClientArtefakte(text: string | null | undefined): string | null {
  if (text == null) return null;
  let ergebnis = text;
  for (const muster of OUTLOOK_ALT_TEXT_MUSTER) {
    ergebnis = ergebnis.replace(muster, "");
  }
  return ergebnis
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ── Mail wie im Mailprogramm (WARTESCHLANGE Punkt 99, Antoine 23.09.2026) ──────────────────────
// „der Anfragentext enthält html, das muss ausgeführt werden, das ist so nicht gut.“ Fremdes
// Mail-HTML ist nicht vertrauenswürdig. Darum ZWEI Schichten: (1) diese Säuberung per DOMParser
// (inertes Dokument: nichts lädt, nichts läuft) wirft aktive Inhalte raus, (2) der Aufrufer zeigt
// das Ergebnis NUR in einem `<iframe srcDoc sandbox="allow-popups allow-popups-to-escape-sandbox">`
// — ohne `allow-scripts`/`allow-same-origin`, dasselbe Prinzip wie `pages/freigaben.tsx`.

/** Die CSP im Rahmen verbietet jedes Nachladen: externe Bilder/Zählpixel, Fonts, Stylesheets.
 *  Nur eingebettete `data:`-Bilder und Inline-Stile bleiben. Eine zweite CSP aus der Mail selbst
 *  könnte das nur weiter verschärfen, nie lockern — und `<meta>` der Mail fliegt ohnehin raus. */
const RAHMEN_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";

const AKTIVE_ELEMENTE = "script,iframe,frame,frameset,object,embed,applet,form,base,meta,link,noscript,template";

/**
 * Säubert Mail-HTML für die Anzeige im abgeschotteten Rahmen: aktive Elemente, `on*`-Attribute
 * und `javascript:`-URLs raus; Links öffnen in neuem Tab mit `rel="noopener noreferrer"`.
 * Liefert ein vollständiges Dokument (mit CSP + Referrer-Sperre) für `srcDoc`.
 */
export function mailHtmlFuerRahmen(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll(AKTIVE_ELEMENTE).forEach((el) => el.remove());
  doc.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (/^on/i.test(attr.name) || /^\s*(javascript|vbscript):/i.test(attr.value)) el.removeAttribute(attr.name);
    }
  });
  doc.querySelectorAll("a").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta http-equiv="Content-Security-Policy" content="${RAHMEN_CSP}">` +
    `<meta name="referrer" content="no-referrer">` +
    `<style>body{margin:0;padding:4px;font:14px/1.5 system-ui,sans-serif;color:#1c1b1f;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}</style>` +
    doc.head.innerHTML +
    `</head><body>${doc.body.innerHTML}</body></html>`
  );
}

export type MailTextTeil = { art: "text" | "zitat"; text: string };

/**
 * Text-Mail lesbar machen: `<https://…>`-Klammern um nackte URLs weg, zusammenhängende
 * Zitatzeilen (`>`/`›`) als eigener Block, damit die Anzeige sie gedämpft/eingeklappt zeigt.
 */
export function gliedereMailText(text: string): MailTextTeil[] {
  const teile: MailTextTeil[] = [];
  for (const zeile of text.replace(/<(https?:\/\/[^\s<>]+)>/g, "$1").split("\n")) {
    const art = /^\s*[>›]/.test(zeile) ? "zitat" : "text";
    const letzte = teile[teile.length - 1];
    if (letzte && letzte.art === art) letzte.text += `\n${zeile}`;
    else teile.push({ art, text: zeile });
  }
  return teile.map((t) => ({ ...t, text: t.text.replace(/^\n+|\n+$/g, "") })).filter((t) => t.text.trim());
}

/** Anzeige-Text für eine lange URL: Host plus „/…“, statt 300 Zeichen Tracking-Pfad. */
export function kuerzeUrl(url: string): string {
  try {
    const u = new URL(url);
    const rest = (u.pathname + u.search).replace(/^\/$/, "");
    return rest.length > 24 ? `${u.host}/…` : `${u.host}${rest}`;
  } catch {
    return url;
  }
}
